import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Alert, Modal, Space, Table, Tag, Upload } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { API } from '@/services/api.service';
import { errorText, saveBlob } from './format';

/**
 * Bank statement PDFs turned into the XML BizniSoft's statement import reads,
 * for clients whose bank sends PDF only. Nothing is imported or stored on the
 * platform.
 *
 * Two steps on one button: with PDFs dropped it reads „Make XML (n)" and
 * converts and downloads at once; afterwards the rows show what became of
 * each PDF and the button reads „Download XML" / „Download ZIP (n)", so the
 * same file can be saved again. Dropping new PDFs starts over.
 */
export default function StatementXmlModal({ open, company, onClose }) {
  const { t } = useTranslation('taxReturns');
  const [files, setFiles] = useState([]);
  const [results, setResults] = useState(null);
  // The last converted file, kept so it can be downloaded again.
  const [output, setOutput] = useState(null);

  const mutation = useMutation({
    mutationFn: () => API.taxReturns.convertStatementsToXml(company, files),
    onSuccess: ({ blob, fileName, results: list }) => {
      const count = list.reduce((s, r) => s + (r.statements?.length || 0), 0);
      saveBlob(blob, fileName);
      setOutput({ blob, fileName, count, zip: /\.zip$/i.test(fileName) });
      setResults(list);
      setFiles([]);
    },
    onError: (error) => {
      setOutput(null);
      setResults(error?.response?.data?.results || null);
    },
  });

  const reset = () => {
    setResults(null);
    setOutput(null);
    mutation.reset();
  };

  const close = () => {
    setFiles([]);
    reset();
    onClose();
  };

  const ready = output && !files.length;
  const okText = ready
    ? output.zip
      ? t('statementXml.downloadZip', { count: output.count })
      : t('statementXml.downloadXml')
    : t('statementXml.submit', { count: files.length });

  return (
    <Modal
      open={open}
      title={t('statementXml.title')}
      width={820}
      onCancel={close}
      okText={okText}
      cancelText={t('import.close')}
      cancelButtonProps={{ shape: 'round' }}
      okButtonProps={{ shape: 'round', disabled: !ready && !files.length, loading: mutation.isPending }}
      onOk={() => (ready ? saveBlob(output.blob, output.fileName) : mutation.mutate())}
      destroyOnClose
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <p className="text-sm opacity-70">{t('statementXml.help')}</p>
        <Upload.Dragger
          multiple
          accept=".pdf"
          fileList={files.map((f) => ({ uid: f.uid, name: f.name, status: 'done' }))}
          beforeUpload={(file) => {
            // New PDFs start a new conversion: the previous result no longer describes what is on screen.
            if (results || output) reset();
            setFiles((list) => [...list, file]);
            return false;
          }}
          onRemove={(item) => setFiles((list) => list.filter((f) => f.uid !== item.uid))}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">{t('statementXml.drop')}</p>
        </Upload.Dragger>
        {mutation.isError && <Alert type="error" showIcon message={errorText(t, mutation.error)} />}
        {ready && <Alert type="success" showIcon message={t('statementXml.downloaded', { fileName: output.fileName })} />}
        {results?.length > 0 && (
          <Table
            size="small"
            tableLayout="fixed"
            rowKey={(r, i) => `${r.fileName}-${i}`}
            dataSource={results}
            pagination={{ pageSize: 10, hideOnSinglePage: true }}
            columns={[
              // Fixed layout with set shares: long file names and messages are cut with „…" (full text on hover), never past the modal.
              { title: t('import.file'), dataIndex: 'fileName', width: '38%', ellipsis: true, render: (v) => <span title={v}>{v}</span> },
              {
                title: t('import.outcome'),
                dataIndex: 'outcome',
                width: '18%',
                render: (v) => <Tag color={v === 'error' ? 'red' : 'green'}>{t(`statementXml.outcomes.${v}`)}</Tag>,
              },
              {
                title: t('import.message'),
                dataIndex: 'message',
                width: '44%',
                ellipsis: true,
                render: (v, r) => {
                  const text = r.code
                    ? `${t(`import.errors.${r.code}`)}${v ? ` (${v})` : ''}`
                    : (r.statements || []).map((s) => t('statementXml.detail', { number: s.number, lines: s.lines })).join('; ');
                  return <span title={text}>{text}</span>;
                },
              },
            ]}
          />
        )}
      </Space>
    </Modal>
  );
}
